import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { api, money, errMsg, COUNTRIES } from "../lib/api";
import { useAuth, useCart } from "../context/AppContext";
import { PageHeader } from "../components/PageHeader";

const EMPTY = { name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "MY", phone: "" };

const Field = ({ label, k, addr, setAddr, req = true, className = "" }) => (
  <div className={className}>
    <label className="wb-label">{label}{req && " *"}</label>
    <input className="wb-input" value={addr[k]} required={req} onChange={(e) => setAddr({ ...addr, [k]: e.target.value })} data-testid={`shipping-${k.replace("_", "-")}-input`} />
  </div>
);

function AddressForm({ addr, setAddr }) {
  const p = { addr, setAddr };
  return (
    <section className="wb-card p-8" data-testid="shipping-address-form">
      <div className="eyebrow mb-6">Shipping address</div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Full name" k="name" {...p} className="sm:col-span-2" />
        <Field label="Address" k="line1" {...p} className="sm:col-span-2" />
        <Field label="Apartment, suite" k="line2" req={false} {...p} className="sm:col-span-2" />
        <Field label="City" k="city" {...p} />
        <Field label="State / region" k="state" req={false} {...p} />
        <Field label="Postal code" k="postal_code" {...p} />
        <div>
          <label className="wb-label">Country *</label>
          <select className="wb-input" value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })} data-testid="shipping-country-select">
            {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select>
        </div>
        <Field label="Phone" k="phone" req={false} {...p} className="sm:col-span-2" />
      </div>
    </section>
  );
}

function useShippingQuote(items, hasPhysical, country) {
  const [q, setQ] = useState(null);
  useEffect(() => {
    if (!items.length) return;
    const payload = items.map((i) => ({ product_id: i.product_id, quantity: i.quantity }));
    api.post("/shipping/quote", { items: payload, country: hasPhysical ? country : null })
      .then((r) => setQ(r.data)).catch((e) => { setQ(null); toast.error(errMsg(e)); });
  }, [items, hasPhysical, country]);
  return q;
}

const EmailSection = ({ user, email, setEmail }) => (
  <section className="wb-card p-8">
    <div className="eyebrow mb-6">{user ? "Signed in" : "Guest checkout"}</div>
    <label className="wb-label">Email for receipt & downloads *</label>
    <input type="email" required className="wb-input" value={email} onChange={(e) => setEmail(e.target.value)} readOnly={!!user} data-testid="checkout-email-input" />
    {!user && (
      <p className="mt-4 text-xs muted">
        Have an account? <Link to="/login?next=/checkout" className="gold underline-offset-4 hover:underline" data-testid="checkout-login-link">Sign in</Link> to keep purchases in My Library.
      </p>
    )}
  </section>
);

function OrderSummary({ items, q, hasPhysical, country, busy }) {
  const blocked = q?.blocked?.length > 0;
  return (
    <aside className="lg:col-span-5 wb-card p-8 h-fit">
      <div className="eyebrow">Order summary</div>
      <div className="mt-6 space-y-3">
        {items.map((i) => (
          <div key={i.product_id} className="flex justify-between gap-4 text-sm">
            <span>{i.title} <span className="muted">× {i.quantity}</span></span>
            <span className="font-mono">{money(i.price * i.quantity)}</span>
          </div>
        ))}
      </div>
      <div className="mt-6 space-y-2 border-t wb-line pt-6 text-sm">
        <div className="flex justify-between muted"><span>Subtotal</span><span className="font-mono" data-testid="checkout-subtotal">{money(q?.subtotal)}</span></div>
        <div className="flex justify-between muted"><span>Shipping {hasPhysical && `(${country})`}</span><span className="font-mono" data-testid="checkout-shipping">{money(q?.shipping_total)}</span></div>
        <div className="flex justify-between pt-2 text-base"><span>Total</span><span className="font-mono gold" data-testid="checkout-total">{money(q?.total)} USD</span></div>
      </div>
      {blocked && (
        <div className="mt-6 border border-red-500/40 p-4 text-xs text-red-400" data-testid="checkout-shipping-blocked">
          {q.blocked.join(". ")}. Remove it or choose a Malaysian address.
        </div>
      )}
      <button type="submit" disabled={busy || blocked || !q} className="btn-gold mt-8 w-full" data-testid="checkout-submit-button">
        <Lock size={14} /> {busy ? "Redirecting to Stripe…" : "Pay securely with Stripe"}
      </button>
      <p className="mt-4 text-center font-mono text-[0.65rem] uppercase tracking-[0.2em] muted">Test mode · card 4242 4242 4242 4242</p>
    </aside>
  );
}

export default function Checkout() {
  const { user } = useAuth();
  const { items, hasPhysical } = useCart();
  const [email, setEmail] = useState("");
  const [addr, setAddr] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const q = useShippingQuote(items, hasPhysical, addr.country);

  useEffect(() => { if (user) setEmail(user.email); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        email: email || null, shipping_address: hasPhysical ? addr : null, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  if (!items.length)
    return <div className="wb-container py-32" data-testid="checkout-empty"><p className="muted">Your cart is empty.</p><Link to="/shop" className="btn-gold mt-8">Explore the shop</Link></div>;

  return (
    <div data-testid="checkout-page">
      <PageHeader eyebrow="Secure checkout" title="Checkout" />
      <form onSubmit={submit} className="wb-container grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7 space-y-8">
          <EmailSection user={user} email={email} setEmail={setEmail} />
          {hasPhysical && <AddressForm addr={addr} setAddr={setAddr} />}
        </div>
        <OrderSummary items={items} q={q} hasPhysical={hasPhysical} country={addr.country} busy={busy} />
      </form>
    </div>
  );
}
